import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Button,
  Spin,
  Tag,
  Space,
  Timeline,
  DatePicker,
  Input,
  Table,
} from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../components/AgGridWithColumnState";
import {
  DollarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BankOutlined,
  RiseOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CommentOutlined,
  SendOutlined,
} from "@ant-design/icons";
// import { Pie, Bar, Line } from "@ant-design/charts";
import moment from "moment";
import {
  getDashboardData,
  getPaymentsDueData,
  dashboardAPI,
} from "../api/dashboard";

import "./AccountingDashboard.css";
import TeamComments from "./Shared/Utils/TeamComments";

const { Option } = Select;
const { RangePicker } = DatePicker;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

const AccountingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  // Comments for Money Sources
  const [vendorComments, setVendorComments] = useState({});
  const [newVendorComment, setNewVendorComment] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Comments for Tasks
  const [taskComments, setTaskComments] = useState({});
  const [newTaskComment, setNewTaskComment] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);

  // Payments Due state (active)
  const [paymentsData, setPaymentsData] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState("weekly");
  const [customDateRange, setCustomDateRange] = useState([]);

  // Payments Due “pending” UI state (Apply button)
  const [pendingPaymentFilter, setPendingPaymentFilter] = useState("weekly");
  const [pendingPaymentRange, setPendingPaymentRange] = useState([]);

  // Payments Due partners state
  const [selectedPartners, setSelectedPartners] = useState([]); // active
  const [pendingPaymentPartners, setPendingPaymentPartners] = useState([]); // pending (Apply)

  // Global filters for Overview
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [globalDateRange, setGlobalDateRange] = useState("monthly");
  const [globalCustomDateRange, setGlobalCustomDateRange] = useState([]);
  const [pendingFilters, setPendingFilters] = useState({
    brands: [],
    dateRange: "monthly",
    customDateRange: [],
  });

  // ---- Fetchers ----
  const fetchDashboardData = async (
    brands = [],
    dateRange = globalDateRange,
    startDate = null,
    endDate = null
  ) => {
    const data = await getDashboardData(brands, dateRange, startDate, endDate);
    setDashboardData(data);
  };

  const fetchPaymentsData = async (
    brands = selectedBrands,
    filter = paymentFilter,
    dateRange = customDateRange,
    partners = selectedPartners
  ) => {
    const startDate =
      filter === "custom" && dateRange?.length === 2
        ? dateRange[0].format("YYYY-MM-DD")
        : null;
    const endDate =
      filter === "custom" && dateRange?.length === 2
        ? dateRange[1].format("YYYY-MM-DD")
        : null;

    const data = await getPaymentsDueData(
      brands,
      filter,
      startDate,
      endDate,
      partners
    );
    setPaymentsData(data);
  };

  // ---------- Money Sources (moved up to avoid TDZ) ----------
  const formatTimestamp = (timestamp) =>
    timestamp ? moment(timestamp).format("MMM DD, YYYY HH:mm") : "—";

  const moneySources = Array.isArray(dashboardData?.money_sources?.sources)
    ? dashboardData.money_sources.sources
    : [];
  const moneySourcesTotal = Number(
    dashboardData?.money_sources?.total_amount || 0
  );

  const getMoneySourcesChartData = () =>
    moneySources.map((s) => ({
      name: s?.source || "Unknown",
      amount: Number(s?.amount || 0),
    }));

  // Derive partner options from available data (moneySources + paymentsData)
  const partnerOptions = useMemo(() => {
    const set = new Map();
    // from money sources
    moneySources.forEach((s) => {
      if (s?.partner_id) set.set(String(s.partner_id), s?.source || "Unknown");
    });
    // from payments due list (covers partners not in top vendors)
    (paymentsData?.payments || []).forEach((p) => {
      if (p?.partner_id)
        set.set(String(p.partner_id), p?.partner_name || "Unknown");
    });
    return Array.from(set.entries()).map(([id, name]) => ({ id, name }));
  }, [moneySources, paymentsData?.payments]);

  // ---- Initial load ----
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const startDate =
          globalDateRange === "custom" && globalCustomDateRange?.length === 2
            ? globalCustomDateRange[0].format("YYYY-MM-DD")
            : null;
        const endDate =
          globalDateRange === "custom" && globalCustomDateRange?.length === 2
            ? globalCustomDateRange[1].format("YYYY-MM-DD")
            : null;

        await Promise.all([
          fetchDashboardData(
            selectedBrands,
            globalDateRange,
            startDate,
            endDate
          ),
          fetchPaymentsData(selectedBrands, paymentFilter, customDateRange),
        ]);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Apply Overview filters (brands/date) ----
  const handleApplyFilters = async () => {
    setRefreshing(true);
    try {
      // Ensure proper date formatting for API call
      const startDate =
        pendingFilters.dateRange === "custom" &&
        pendingFilters.customDateRange?.length === 2
          ? pendingFilters.customDateRange[0].format("YYYY-MM-DD")
          : null;
      const endDate =
        pendingFilters.dateRange === "custom" &&
        pendingFilters.customDateRange?.length === 2
          ? pendingFilters.customDateRange[1].format("YYYY-MM-DD")
          : null;

      // Update state with new filter values
      setSelectedBrands(pendingFilters.brands);
      setGlobalDateRange(pendingFilters.dateRange);
      setGlobalCustomDateRange(pendingFilters.customDateRange);

      console.log("Applying filters:", {
        brands: pendingFilters.brands,
        dateRange: pendingFilters.dateRange,
        startDate,
        endDate,
      });

      // Force refresh dashboard data with new filters - direct API call
      const data = await dashboardAPI.getOverview(
        pendingFilters.brands,
        pendingFilters.dateRange,
        startDate,
        endDate
      );

      // Directly update dashboard data with fresh API response
      setDashboardData(data);

      // Also refresh payments data to keep everything in sync
      await fetchPaymentsData(
        pendingFilters.brands,
        paymentFilter,
        customDateRange,
        selectedPartners
      );
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setRefreshing(false);
    }
  };

  // ---- Apply Payments Due filters ----
  const handleApplyPayments = async () => {
    setRefreshing(true);
    try {
      setPaymentFilter(pendingPaymentFilter);
      setCustomDateRange(pendingPaymentRange);
      setSelectedPartners(pendingPaymentPartners);
      await fetchPaymentsData(
        selectedBrands,
        pendingPaymentFilter,
        pendingPaymentRange,
        pendingPaymentPartners
      );
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const startDate =
        globalDateRange === "custom" && globalCustomDateRange?.length === 2
          ? globalCustomDateRange[0].format("YYYY-MM-DD")
          : null;
      const endDate =
        globalDateRange === "custom" && globalCustomDateRange?.length === 2
          ? globalCustomDateRange[1].format("YYYY-MM-DD")
          : null;

      await Promise.all([
        fetchDashboardData(selectedBrands, globalDateRange, startDate, endDate),
        fetchPaymentsData(
          selectedBrands,
          paymentFilter,
          customDateRange,
          selectedPartners
        ),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  // ---- Helpers ----
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "red";
      case "medium":
        return "orange";
      case "low":
        return "green";
      default:
        return "blue";
    }
  };

  // Format currency values
  const formatCurrency = (value, compact = false) => {
    if (!value && value !== 0) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      notation: compact ? "compact" : "standard",
    }).format(value);
  };

  // Build line data from backend timeseries (preferred), or a simple fallback
  const buildFallbackTrend = (financialSummary) => {
    if (!financialSummary) return [];
    const dates = Array.from({ length: 6 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (5 - i) * 5);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    });
    const invoiceTotal = Number(financialSummary.period_total_invoices || 0);
    const billTotal = Number(financialSummary.period_total_bills || 0);

    const toPoints = (label, total) =>
      dates.map((date, idx) => ({
        type: label,
        date,
        value: (total * (idx + 1)) / dates.length,
      }));

    return [
      ...toPoints("Invoices", invoiceTotal),
      ...toPoints("Bills", billTotal),
    ];
  };

  const timeseries = dashboardData?.invoice_bill_timeseries?.daily || [];

  const trendLineData = useMemo(() => {
    if (timeseries.length) {
      return [
        ...timeseries.map((d) => ({
          date: d.date,
          type: "Invoices",
          value: Number(d.invoice_amount || 0),
        })),
        ...timeseries.map((d) => ({
          date: d.date,
          type: "Bills",
          value: Number(d.bill_amount || 0),
        })),
      ];
    }
    return buildFallbackTrend(dashboardData?.financial_summary);
  }, [timeseries, dashboardData?.financial_summary]);

  const moneySourcesColumns = [
    { title: "Vendor", dataIndex: "source", key: "source" },
    {
      title: "Bills",
      dataIndex: "count",
      key: "count",
      align: "right",
      width: 120,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 160,
      render: (val) => formatCurrency(val),
    },
  ];

  // Cash Flow
  const getCashFlowChartData = () => {
    const f = dashboardData?.financial_summary;
    if (!f) return [];
    return [
      { type: "Inflow", amount: f.cash_inflow },
      { type: "Outflow", amount: f.cash_outflow },
      { type: "Net", amount: f.net_cash_flow },
    ];
  };

  // Payments Due table
  const paymentsColumns = [
    {
      title: "Invoice #",
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 140,
    },
    {
      title: "Partner",
      dataIndex: "partner_name",
      key: "partner_name",
      ellipsis: true,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (a) => formatCurrency(a),
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      render: (date) => (date ? moment(date).format("MMM DD, YYYY") : "—"),
      width: 160,
    },
    {
      title: "Days Until Due",
      dataIndex: "days_until_due",
      key: "days_until_due",
      align: "center",
      width: 160,
      render: (days, record) => {
        if (days === null || days === undefined) {
          return <Tag color="blue">{record.payment_term || "No Terms"}</Tag>;
        }
        const color = days < 0 ? "red" : days <= 7 ? "orange" : "green";
        const text = days < 0 ? `${Math.abs(days)} overdue` : `${days} days`;
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Payment Term",
      dataIndex: "payment_term",
      key: "payment_term",
      width: 180,
      render: (term) => term || "No Terms",
    },
  ];

  // ---- TOTAL row for Payments Due (full dataset, not just page) ----
  const totalPaymentsAmount = useMemo(() => {
    return (paymentsData?.payments || []).reduce(
      (sum, p) => sum + Number(p.amount || 0),
      0
    );
  }, [paymentsData?.payments]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="accounting-dashboard">
      <div className="dashboard-header">
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Accounting Dashboard</h1>
            <p>Last updated: {formatTimestamp(dashboardData?.timestamp)}</p>
            {globalDateRange !== "monthly" && (
              <p>
                Date Range:{" "}
                {dashboardData?.unpaid_bills?.date_range ||
                  dashboardData?.allocations_by_user?.date_range ||
                  "—"}
              </p>
            )}
          </Col>
          <Col>
            <Space wrap>
              {/* Brands */}
              <Select
                mode="multiple"
                placeholder="Select Brands"
                style={{ minWidth: 220 }}
                value={pendingFilters.brands}
                onChange={(brands) =>
                  setPendingFilters((p) => ({ ...p, brands }))
                }
                allowClear
              >
                {dashboardData?.brands?.map((brand) => (
                  <Option key={brand.id} value={brand.id}>
                    {brand.name}
                  </Option>
                ))}
              </Select>

              {/* Partners */}
              {/* <Select
                mode="multiple"
                placeholder="Filter by Partners"
                style={{ minWidth: 240 }}
                value={pendingPaymentPartners}
                onChange={setPendingPaymentPartners}
                allowClear
                maxTagCount="responsive"
              >
                {partnerOptions.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name}
                  </Option>
                ))}
              </Select> */}

              {/* Overview date range */}
              <Select
                value={pendingFilters.dateRange}
                onChange={(val) =>
                  setPendingFilters((p) => ({
                    ...p,
                    dateRange: val,
                    customDateRange: val !== "custom" ? [] : p.customDateRange,
                  }))
                }
                style={{ width: 140 }}
              >
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
                <Option value="custom">Custom</Option>
              </Select>

              {pendingFilters.dateRange === "custom" && (
                <RangePicker
                  value={pendingFilters.customDateRange}
                  onChange={(dates) =>
                    setPendingFilters((p) => ({
                      ...p,
                      customDateRange: dates || [],
                    }))
                  }
                  format="YYYY-MM-DD"
                  allowClear
                />
              )}

              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleApplyFilters}
                loading={refreshing}
              >
                Apply Filters
              </Button>

              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={refreshing}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* KPI Cards - Updated with new financial summary cards */}
      <Row gutter={[16, 16]} className="metrics-row">
        {/* Pending Allocations Card */}
        <Col xs={24} sm={12} lg={4}>
          <Card className="metric-card pending-allocations">
            <Statistic
              title="Pending Allocations"
              value={dashboardData?.pending_allocations?.count || 0}
              prefix={<ExclamationCircleOutlined />}
              suffix="transactions"
            />
            <div className="metric-timestamp">
              Updated:{" "}
              {formatTimestamp(
                dashboardData?.pending_allocations?.last_updated
              )}
            </div>
          </Card>
        </Col>

        {/* Unpaid Bills Card */}
        <Col xs={24} sm={12} lg={4}>
          <Card className="metric-card unpaid-bills">
            <Statistic
              title="Unpaid Bills"
              value={dashboardData?.unpaid_bills?.count || 0}
              prefix={<FileTextOutlined />}
              suffix="bills"
            />
            <div className="metric-amount">
              Total:{" "}
              {formatCurrency(dashboardData?.unpaid_bills?.total_amount || 0)}
            </div>
            <div className="metric-timestamp">
              Updated:{" "}
              {formatTimestamp(dashboardData?.unpaid_bills?.last_updated)}
            </div>
          </Card>
        </Col>

        {/* Total Bills Card */}
        <Col xs={24} sm={12} lg={4}>
          <Card className="metric-card unpaid-bills">
            <Statistic
              title="Total Bills"
              value={dashboardData?.financial_summary?.total_bills_count || 0}
              prefix={<FileTextOutlined />}
              suffix="bills"
            />
            <div className="metric-amount">
              Total:{" "}
              {formatCurrency(
                dashboardData?.financial_summary?.total_bills || 0
              )}
            </div>
            <div className="metric-timestamp">
              {dashboardData?.financial_summary?.period || "N/A"}
            </div>
          </Card>
        </Col>

        {/* Total Invoices Card */}
        <Col xs={24} sm={12} lg={4}>
          <Card className="metric-card financial-summary">
            <Statistic
              title="Total Invoices"
              value={
                dashboardData?.financial_summary?.total_invoices_count || 0
              }
              prefix={<DollarOutlined />}
              suffix="invoices"
            />
            <div className="metric-amount">
              Total:{" "}
              {formatCurrency(
                dashboardData?.financial_summary?.total_invoices || 0
              )}
            </div>
            <div className="metric-timestamp">
              {dashboardData?.financial_summary?.period || "N/A"}
            </div>
          </Card>
        </Col>

        {/* Net Income Card */}
        <Col xs={24} sm={12} lg={4}>
          <Card className="metric-card financial-summary">
            <Statistic
              title="Net Income"
              value={dashboardData?.financial_summary?.net_income || 0}
              prefix={<RiseOutlined />}
              precision={getDecimalPrecision()}
              formatter={(value) => formatCurrency(value)}
            />
            <div className="metric-timestamp">
              {dashboardData?.financial_summary?.period || "N/A"}
            </div>
            <div className="metric-breakdown">
              <div>
                Invoices:{" "}
                {formatCurrency(
                  dashboardData?.financial_summary?.total_invoices || 0
                )}
              </div>
              <div>
                Bills:{" "}
                {formatCurrency(
                  dashboardData?.financial_summary?.total_bills || 0
                )}
              </div>
            </div>
          </Card>
        </Col>

        {/* Bank Account Balance Card */}
        {/* <Col xs={24} sm={12} lg={4}>
          <Card className="metric-card financial-summary">
            <Statistic
              title="Bank Balance"
              value={dashboardData?.financial_summary?.bank_balance || 0}
              prefix={<BankOutlined />}
              precision={getDecimalPrecision()}
              formatter={(value) => formatCurrency(value)}
            />
            <div className="metric-description">
              As of:{" "}
              {dashboardData?.financial_summary?.bank_statement_date
                ? moment(
                    dashboardData.financial_summary.bank_statement_date
                  ).format("MMM DD, YYYY")
                : "N/A"}
            </div>
            <div className="metric-timestamp">
              Updated:{" "}
              {formatTimestamp(dashboardData?.financial_summary?.last_updated)}
            </div>
          </Card>
        </Col> */}

        {/* Bank Account Balance Card */}
        <Col xs={24} sm={12} lg={4}>
          <Card className="metric-card financial-summary">
            <Statistic
              title="Bank Balance (Total)"
              value={dashboardData?.financial_summary?.bank_balance_total || 0}
              prefix={<BankOutlined />}
              precision={2}
              formatter={(value) => formatCurrency(value)}
            />

            {/* List each bank and its balance */}
            <div className="metric-description" style={{ marginTop: 8 }}>
              {(dashboardData?.financial_summary?.bank_accounts || []).length >
              0 ? (
                <div style={{ lineHeight: 1.4 }}>
                  {dashboardData.financial_summary.bank_accounts.map((b) => (
                    <div
                      key={b.bank_id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        title={b.bank_name}
                        style={{
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {b.bank_name}
                      </span>
                      <span>{formatCurrency(b.balance)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span>No bank data</span>
              )}
            </div>

            <div className="metric-timestamp" style={{ marginTop: 6 }}>
              Updated:{" "}
              {formatTimestamp(dashboardData?.financial_summary?.last_updated)}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Three Column Row: Tasks, Money Sources, Financial Trends */}
      <Row gutter={[16, 16]} className="content-row">
        {/* Column 1: Team Comments */}
        <Col xs={24} lg={6}>
          <TeamComments
            selectedBrand={
              selectedBrands.length > 0 ? { id: selectedBrands[0] } : null
            }
            showStats={true}
            maxHeight={500}
          />
        </Col>

        {/* Column 1.5: Tasks (Reduced) */}
        <Col xs={24} lg={6}>
          <Card title="Tasks" className="next-actions-card">
            <div className="user-tasks">
              <h4>
                <UserOutlined /> Paul&apos;s Tasks
              </h4>
              {dashboardData?.next_actions?.paul?.map((task, i) => (
                <div
                  key={`paul-${i}`}
                  className={`task-item ${
                    selectedTask === `paul-${i}` ? "selected" : ""
                  }`}
                  onClick={() =>
                    setSelectedTask(
                      selectedTask === `paul-${i}` ? null : `paul-${i}`
                    )
                  }
                >
                  <div className="task-header">
                    <span className="task-name">
                      {task.task}
                      {taskComments[`paul-${i}`]?.length > 0 && (
                        <Tag
                          icon={<CommentOutlined />}
                          color="blue"
                          style={{ marginLeft: 8, cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTask(
                              selectedTask === `paul-${i}` ? null : `paul-${i}`
                            );
                          }}
                        >
                          {taskComments[`paul-${i}`]?.length}
                        </Tag>
                      )}
                    </span>
                    <Tag color={getPriorityColor(task.priority)}>
                      {task.priority}
                    </Tag>
                  </div>
                  <div className="task-details">
                    <span className="task-count">{task.count} items</span>
                    <span className="task-time">~{task.estimated_time}</span>
                  </div>

                  {selectedTask === `paul-${i}` && (
                    <div className="task-comments">
                      <div
                        className="comment-input"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Input.TextArea
                          rows={2}
                          placeholder="Add a comment..."
                          value={newTaskComment}
                          onChange={(e) => setNewTaskComment(e.target.value)}
                          style={{ marginBottom: 8 }}
                        />
                        <Button
                          type="primary"
                          size="small"
                          icon={<SendOutlined />}
                          onClick={() => {
                            if (newTaskComment.trim()) {
                              const timestamp = new Date().toISOString();
                              setTaskComments((prev) => ({
                                ...prev,
                                [`paul-${i}`]: [
                                  ...(prev[`paul-${i}`] || []),
                                  {
                                    text: newTaskComment,
                                    timestamp,
                                    user: "You",
                                  },
                                ],
                              }));
                              setNewTaskComment("");
                            }
                          }}
                        >
                          Add Comment
                        </Button>
                      </div>

                      <div className="comments-list">
                        {taskComments[`paul-${i}`]?.map((comment, idx) => (
                          <div key={idx} className="comment">
                            <div className="comment-header">
                              <span className="comment-user">
                                {comment.user}
                              </span>
                              <span className="comment-time">
                                {new Date(comment.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <div className="comment-text">{comment.text}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <h4 style={{ marginTop: 20 }}>
                <UserOutlined /> Intern&apos;s Tasks
              </h4>
              {dashboardData?.next_actions?.intern?.map((task, i) => (
                <div
                  key={`intern-${i}`}
                  className={`task-item ${
                    selectedTask === `intern-${i}` ? "selected" : ""
                  }`}
                  onClick={() =>
                    setSelectedTask(
                      selectedTask === `intern-${i}` ? null : `intern-${i}`
                    )
                  }
                >
                  <div className="task-header">
                    <span className="task-name">
                      {task.task}
                      {taskComments[`intern-${i}`]?.length > 0 && (
                        <Tag
                          icon={<CommentOutlined />}
                          color="blue"
                          style={{ marginLeft: 8, cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTask(
                              selectedTask === `intern-${i}`
                                ? null
                                : `intern-${i}`
                            );
                          }}
                        >
                          {taskComments[`intern-${i}`]?.length}
                        </Tag>
                      )}
                    </span>
                    <Tag color={getPriorityColor(task.priority)}>
                      {task.priority}
                    </Tag>
                  </div>
                  <div className="task-details">
                    <span className="task-count">{task.count} items</span>
                    <span className="task-time">~{task.estimated_time}</span>
                  </div>

                  {selectedTask === `intern-${i}` && (
                    <div className="task-comments">
                      <div
                        className="comment-input"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Input.TextArea
                          rows={2}
                          placeholder="Add a comment..."
                          value={newTaskComment}
                          onChange={(e) => setNewTaskComment(e.target.value)}
                          style={{ marginBottom: 8 }}
                        />
                        <Button
                          type="primary"
                          size="small"
                          icon={<SendOutlined />}
                          onClick={() => {
                            if (newTaskComment.trim()) {
                              const timestamp = new Date().toISOString();
                              setTaskComments((prev) => ({
                                ...prev,
                                [`intern-${i}`]: [
                                  ...(prev[`intern-${i}`] || []),
                                  {
                                    text: newTaskComment,
                                    timestamp,
                                    user: "You",
                                  },
                                ],
                              }));
                              setNewTaskComment("");
                            }
                          }}
                        >
                          Add Comment
                        </Button>
                      </div>

                      <div className="comments-list">
                        {taskComments[`intern-${i}`]?.map((comment, idx) => (
                          <div key={idx} className="comment">
                            <div className="comment-header">
                              <span className="comment-user">
                                {comment.user}
                              </span>
                              <span className="comment-time">
                                {new Date(comment.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <div className="comment-text">{comment.text}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="card-timestamp">
              Updated:{" "}
              {formatTimestamp(dashboardData?.next_actions?.last_updated)}
            </div>
          </Card>
        </Col>

        {/* Column 2: Money Sources Breakdown with Comments */}
        <Col xs={24} lg={6}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Money Sources Breakdown</span>
                <Tag icon={<CommentOutlined />} color="blue">
                  {Object.keys(vendorComments).length} Comments
                </Tag>
              </div>
            }
            className="money-sources-card"
          >
            {moneySources.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div style={{ flex: 1, minHeight: 200 }}>
                  <Table
                    dataSource={moneySources}
                    columns={moneySourcesColumns}
                    rowKey={(r) => r.partner_id}
                    pagination={{ pageSize: 5, showSizeChanger: false }}
                    size="small"
                    scroll={{ y: 200 }}
                    onRow={(record) => ({
                      onClick: () => {
                        // Set the selected vendor for comments
                        setSelectedVendor(record.source); // <-- use source
                        setNewVendorComment("");
                      },
                      style: {
                        cursor: "pointer",
                        backgroundColor:
                          selectedVendor === record.source
                            ? "#f0f7ff"
                            : "inherit",
                      },
                    })}
                  />
                </div>

                <div
                  style={{
                    marginTop: 16,
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: 16,
                  }}
                >
                  <div style={{ marginBottom: 12, fontWeight: 500 }}>
                    {selectedVendor
                      ? `Comments for ${selectedVendor}`
                      : "Select a vendor to add comments"}
                  </div>

                  {selectedVendor && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: "flex", marginBottom: 8 }}>
                        <Input.TextArea
                          rows={2}
                          placeholder="Add a comment..."
                          value={newVendorComment}
                          onChange={(e) => setNewVendorComment(e.target.value)}
                          style={{ flex: 1, marginRight: 8 }}
                        />
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          onClick={() => {
                            if (newVendorComment.trim()) {
                              const timestamp = new Date().toISOString();
                              setVendorComments((prev) => ({
                                ...prev,
                                [selectedVendor]: [
                                  ...(prev[selectedVendor] || []),
                                  {
                                    text: newVendorComment,
                                    timestamp,
                                    user: "Current User",
                                  },
                                ],
                              }));
                              setNewVendorComment("");
                            }
                          }}
                        >
                          Post
                        </Button>
                      </div>

                      <div style={{ maxHeight: 150, overflowY: "auto" }}>
                        {vendorComments[selectedVendor]?.map((comment, idx) => (
                          <div
                            key={idx}
                            style={{
                              marginBottom: 8,
                              padding: 8,
                              backgroundColor: "#f9f9f9",
                              borderRadius: 4,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                color: "#666",
                                marginBottom: 4,
                              }}
                            >
                              {comment.user} •{" "}
                              {new Date(comment.timestamp).toLocaleString()}
                            </div>
                            <div>{comment.text}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="card-timestamp" style={{ marginTop: 8 }}>
                  Total Vendors: {moneySources.length}
                </div>
              </div>
            ) : (
              <div className="no-data">No vendor data available</div>
            )}
          </Card>
        </Col>

        {/* Column 3: Financial Trends Line Graph */}
        <Col xs={24} lg={6}>
          <Card
            title="Financial Trends"
            className="financial-trends-card"
            extra={
              <Select
                defaultValue="monthly"
                style={{ width: 100 }}
                onChange={(value) => {
                  // Handle period change for the line graph
                  console.log("Period changed:", value);
                }}
              >
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
                <Option value="quarterly">Quarterly</Option>
              </Select>
            }
          >
            {dashboardData?.financial_summary ? (
              <div className="financial-trends-content">
                <div className="trends-summary">
                  <div className="trend-item">
                    <div className="trend-label">Invoices</div>
                    <div className="trend-value">
                      {dashboardData?.financial_summary
                        ?.period_total_invoices_count || 0}{" "}
                      invoices
                      <span className="trend-amount">
                        {formatCurrency(
                          dashboardData?.financial_summary
                            ?.period_total_invoices || 0
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="trend-item">
                    <div className="trend-label">Bills</div>
                    <div className="trend-value">
                      {dashboardData?.financial_summary
                        ?.period_total_bills_count || 0}{" "}
                      bills
                      <span className="trend-amount">
                        {formatCurrency(
                          dashboardData?.financial_summary
                            ?.period_total_bills || 0
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* <div
                  className="line-chart-container"
                  style={{ height: 200, marginTop: 15 }}
                >
                  {dashboardData?.financial_summary ? (
                    <Line
                      data={trendLineData}
                      xField="date"
                      yField="value"
                      seriesField="type"
                      point={{}}
                      legend={{
                        position: "top",
                      }}
                      tooltip={{
                        formatter: (datum) => {
                          return {
                            name: datum.type,
                            value: formatCurrency(datum.value),
                          };
                        },
                      }}
                      yAxis={{
                        label: {
                          formatter: (v) => formatCurrency(v, true),
                        },
                      }}
                    />
                  ) : (
                    <div
                      className="chart-placeholder"
                      style={{ textAlign: "center", padding: "80px 0" }}
                    >
                      No financial trend data available
                    </div>
                  )}
                </div> */}

                <div className="card-timestamp">
                  Period: {dashboardData?.financial_summary?.period || "N/A"}
                </div>
              </div>
            ) : (
              <div className="no-data">No financial trend data available</div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Allocations + Unallocated Bank Transactions */}
      <Row gutter={[16, 16]} className="content-row">
        <Col xs={24} lg={12}>
          <Card title="Allocations by User" className="allocations-card">
            {dashboardData?.allocations_by_user?.users?.length > 0 ? (
              <div>
                <div className="allocations-summary">
                  <Row gutter={[16, 16]}>
                    {dashboardData.allocations_by_user.users.map(
                      (user, index) => (
                        <Col key={index} xs={24} sm={12}>
                          <Card size="small" className="user-allocation-card">
                            <Statistic
                              title={user.user}
                              value={user.amount}
                              precision={getDecimalPrecision()}
                              formatter={(value) => formatCurrency(value)}
                              prefix={<UserOutlined />}
                            />
                            <div className="user-details">
                              <span>
                                {user.count} allocations ({user.percentage}%)
                              </span>
                              <Tag
                                color={
                                  user.attribution_type === "real_user"
                                    ? "green"
                                    : "blue"
                                }
                              >
                                {user.attribution_type === "real_user"
                                  ? "Action History"
                                  : "Time-based"}
                              </Tag>
                            </div>
                          </Card>
                        </Col>
                      )
                    )}
                  </Row>
                </div>

                <div className="allocation-breakdown">
                  <h4>
                    Total Allocated:{" "}
                    {formatCurrency(
                      dashboardData.allocations_by_user.total_allocated
                    )}
                  </h4>
                  {dashboardData.allocations_by_user.date_range && (
                    <p>
                      Period: {dashboardData.allocations_by_user.date_range}
                    </p>
                  )}
                </div>

                <div className="card-timestamp">
                  Updated:{" "}
                  {formatTimestamp(
                    dashboardData?.allocations_by_user?.last_updated
                  )}
                </div>
              </div>
            ) : (
              <div className="no-data">
                No allocation data available for the selected period
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title="Unallocated Bank Transactions"
            className="unallocated-transactions-card"
          >
            {dashboardData?.unallocated_transactions ? (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <Statistic
                      title="Total Count"
                      value={dashboardData.unallocated_transactions.total_count}
                      prefix={<FileTextOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Money In"
                      value={
                        dashboardData.unallocated_transactions.positive_amount
                      }
                      precision={getDecimalPrecision()}
                      formatter={(value) => formatCurrency(value)}
                      prefix={<RiseOutlined />}
                      valueStyle={{ color: "#3f8600" }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Money Out"
                      value={
                        dashboardData.unallocated_transactions.negative_amount
                      }
                      precision={getDecimalPrecision()}
                      formatter={(value) => formatCurrency(value)}
                      prefix={<WarningOutlined />}
                      valueStyle={{ color: "#cf1322" }}
                    />
                  </Col>
                </Row>

                <Table
                  dataSource={
                    dashboardData.unallocated_transactions
                      .recent_transactions || []
                  }
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 2, showSizeChanger: false }}
                  columns={[
                    {
                      title: "Date",
                      dataIndex: "transaction_date",
                      key: "transaction_date",
                      render: (d) =>
                        d ? moment(d).format("MMM DD, YYYY") : "—",
                      width: 160,
                    },
                    {
                      title: "Description",
                      dataIndex: "description",
                      key: "description",
                      ellipsis: true,
                    },
                    {
                      title: "Brand",
                      dataIndex: "brand_name",
                      key: "brand_name",
                      width: 160,
                    },
                    {
                      title: "Type",
                      dataIndex: "type",
                      key: "type",
                      width: 100,
                      render: (t) => (
                        <Tag color={t === "inflow" ? "green" : "red"}>{t}</Tag>
                      ),
                    },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      key: "amount",
                      align: "right",
                      render: (a) => formatCurrency(a),
                      width: 140,
                    },
                  ]}
                />

                <div className="card-timestamp" style={{ marginTop: 8 }}>
                  Updated:{" "}
                  {formatTimestamp(
                    dashboardData.unallocated_transactions.last_updated
                  )}
                </div>
              </>
            ) : (
              <div className="no-data">
                No unallocated transactions data available
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Payments Due */}
      <Row gutter={[16, 16]} className="content-row">
        <Col span={24}>
          <Card title="Payments Due" className="payments-due-card">
            <Row
              align="middle"
              justify="space-between"
              style={{ marginBottom: 12 }}
            >
              <Col flex="1 1 auto">
                <Space size="large" wrap>
                  <Statistic
                    title="Total Due"
                    value={paymentsData?.total_amount_due || 0}
                    precision={2}
                    formatter={(value) => formatCurrency(value)}
                    prefix={<DollarOutlined />}
                  />
                  <span className="payments-count">
                    {paymentsData?.payments_count || 0} payments
                  </span>
                </Space>
              </Col>

              <Col>
                <Space wrap>
                  <Select
                    mode="multiple"
                    placeholder="Filter by partner"
                    value={pendingPaymentPartners}
                    onChange={setPendingPaymentPartners}
                    style={{ width: 300 }}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {paymentsData?.available_partners?.map((partner) => (
                      <Option key={partner.id} value={partner.id}>
                        {partner.name}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    value={pendingPaymentFilter}
                    onChange={setPendingPaymentFilter}
                    style={{ width: 140 }}
                  >
                    <Option value="weekly">Weekly</Option>
                    <Option value="monthly">Monthly</Option>
                    <Option value="custom">Custom</Option>
                  </Select>

                  {pendingPaymentFilter === "custom" && (
                    <RangePicker
                      value={pendingPaymentRange}
                      onChange={(dates) => setPendingPaymentRange(dates || [])}
                      format="YYYY-MM-DD"
                      allowClear
                    />
                  )}

                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleApplyPayments}
                    loading={refreshing}
                  >
                    Apply
                  </Button>
                </Space>
              </Col>
            </Row>

            <Table
              dataSource={paymentsData?.payments || []}
              columns={paymentsColumns}
              rowKey="invoice_id"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 900 }}
              size="small"
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <strong>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <div style={{ textAlign: "right" }}>
                        <strong>{formatCurrency(totalPaymentsAmount)}</strong>
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5} />
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            <div className="card-timestamp">
              Updated: {formatTimestamp(paymentsData?.last_updated)}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Invoice History */}
      <Row gutter={[16, 16]} className="content-row">
        <Col xs={24} lg={12}>
          <Card title="Invoice History" className="invoice-history-card">
            <div className="history-summary" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Invoices Created"
                    value={
                      dashboardData?.invoice_history?.invoices_count_in_range ||
                      0
                    }
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: "#3f8600" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Bills Created"
                    value={
                      dashboardData?.invoice_history?.bills_count_in_range || 0
                    }
                    prefix={<FileTextOutlined />}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Total Created"
                    value={
                      dashboardData?.invoice_history?.total_count_in_range || 0
                    }
                    prefix={<RiseOutlined />}
                  />
                </Col>
              </Row>

              {dashboardData?.invoice_history?.date_range && (
                <p style={{ marginTop: 8 }}>
                  Period: {dashboardData.invoice_history.date_range}
                </p>
              )}

              <div>
                Last Invoice Created:{" "}
                {dashboardData?.invoice_history?.last_created
                  ? formatTimestamp(dashboardData.invoice_history.last_created)
                  : "Never"}
              </div>
            </div>

            <div
              className="ag-theme-alpine"
              style={{ height: 300, width: "100%" }}
            >
              <AgGridWithColumnState
                tableId="accounting-dashboard-recent-invoices"
                rowData={dashboardData?.invoice_history?.recent_invoices || []}
                columnDefs={[
                  {
                    headerName: "Number",
                    field: "invoice_number",
                    width: 160,
                    sortable: true,
                    filter: true,
                  },
                  {
                    headerName: "Partner",
                    field: "partner_name",
                    width: 100,
                    sortable: true,
                    filter: true,
                  },
                  {
                    headerName: "Type",
                    field: "type",
                    width: 100,
                    sortable: true,
                    filter: true,
                    cellRenderer: (params) => (
                      <Tag
                        color={params.value === "invoice" ? "green" : "blue"}
                        style={{ textTransform: "capitalize" }}
                      >
                        {params.value || "—"}
                      </Tag>
                    ),
                  },
                  {
                    headerName: "Amount",
                    field: "amount",
                    width: 140,
                    sortable: true,
                    filter: "agNumberColumnFilter",
                    cellRenderer: (params) => formatCurrency(params.value),
                  },
                  {
                    headerName: "Created",
                    field: "created_at",
                    width: 180,
                    sortable: true,
                    filter: "agDateColumnFilter",
                    cellRenderer: (params) =>
                      params.value
                        ? moment(params.value).format("MMM DD, YYYY HH:mm")
                        : "—",
                  },
                ]}
                defaultColDef={{
                  resizable: true,
                }}
                animateRows={true}
                rowSelection="single"
                suppressRowClickSelection={true}
                getRowId={(params) => params.data.id}
                onGridReady={(params) => {
                  params.api.sizeColumnsToFit();
                }}
              />
            </div>

            <div className="card-timestamp" style={{ marginTop: 8 }}>
              Updated:{" "}
              {formatTimestamp(dashboardData?.invoice_history?.last_updated)}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AccountingDashboard;
